import { createFileRoute, Link } from "@tanstack/react-router";
import { absUrl, canonicalLink, canonicalUrl, pathFromMatch, routeMeta } from "@/lib/seo";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/hunting-fabric-blind-duck-hunter.jpg";
import heroPicture from "@/assets/hunting-fabric-blind-duck-hunter.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import huntingDeerBlind from "@/assets/hunting-gallery-deer-blind.jpg";
import huntingDuckBoat from "@/assets/hunting-gallery-duck-boat-tarp.jpg";
import huntingQuailBag from "@/assets/hunting-gallery-quail-bag.jpg";
import huntingCraneDecoy from "@/assets/hunting-gallery-crane-decoy-bag.jpg";
import huntingBowhunter from "@/assets/hunting-gallery-bowhunter.png";
import huntingTeam from "@/assets/hunting-team-truck.png";
import fishingTrout from "@/assets/fishing-trout-spread.jpg";
import fishingRedfish from "@/assets/fishing-redfish.jpg";
import coolerCushion from "@/assets/cooler-cushion-rtic.png";
import utvRollCurtain from "@/assets/enclosed-trailer-roll-down-curtain-dirt-bike.jpg";
import airboatRedfish from "@/assets/airboat-redfish-sunset-marsh.jpg";
import truckHighRackBlind from "@/assets/hunting-truck-high-rack-camo-blind.jpg";
import quailTruckSunset from "@/assets/hunting-quail-truck-sunset-south-texas.jpg";
import aluminumDogBoxBed from "@/assets/aluminum-dog-box-truck-bed-storage.jpg";
import huntingTruckHighSeats from "@/assets/custom-hunting-truck-orange-tarp-high-seats-dog-box-seguin.jpg";
import { HuntingFishingExplorer } from "@/components/HuntingFishingExplorer";
import { BoatCoverForm } from "@/components/BoatCoverForm";
import { CoolerCushionForm } from "@/components/CoolerCushionForm";
import { HuntingTarpForm } from "@/components/HuntingTarpForm";

const PAGE_PATH = "/hunting-fishing-covers";

const GALLERY_IMAGES: Array<{ src: string; alt: string; caption: string }> = [
  { src: huntingDeerBlind, alt: "Custom tan Sunbrella canvas cover patterned to a Texas box deer blind, with reinforced corners and tie-downs, set in South Texas brush country at golden-hour sunset", caption: "Custom deer blind canvas cover — heavy Sunbrella, patterned to your blind, built in Seguin, TX for South Texas brush country hunters." },
  { src: huntingDuckBoat, alt: "Heavy navy canvas tarp covering a duck hunting boat on a Texas marsh at dawn", caption: "Heavy canvas duck-boat tarp — Texas marsh" },
  { src: huntingQuailBag, alt: "Custom canvas hunting bag with leather trim and shotgun shells — specialty hunting items by Texas Made Shade", caption: "Custom canvas quail / shell hunting bag" },
  { src: huntingCraneDecoy, alt: "Custom canvas game bird decoy bag full of sandhill crane decoys in a Texas field at sunrise", caption: "Sandhill crane decoy bag — Texas field" },
];

export const Route = createFileRoute("/hunting-fishing-covers")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Texas Hunting Covers | Deer Blinds, UTV Curtains, Coolers & Dog Boxes | Seguin TX",
      description:
        "Custom hunting covers in Seguin, TX — deer blind skins, UTV/ATV weather curtains, YETI wraps, gun & bow covers and welded dog boxes.",
      image: hero,
      imageAlt: "Custom Texas hunting canvas covers — deer blinds, UTV curtains and welded dog boxes by Texas Made Shade",
      socialTitle: "Custom Hunting Covers in Texas — Deer Blinds, UTV Curtains, Dog Boxes",
      socialDescription:
        "Deer blind skins, UTV weather curtains, cooler wraps, gun covers and welded dog boxes — built in Seguin, TX for the Texas Hill Country and South Texas brush.",
      keywords:
        "hunting covers Texas, deer blind covers, deer blind roof skins, custom YETI cooler covers, gun covers, bow and arrow covers, UTV weather curtains, Polaris Ranger covers, Can-Am Defender covers, welded dog boxes, hunting truck upholstery, Sunbrella Marine canvas, Top Gun fabric, Aqualon Edge, Seguin TX, San Antonio, New Braunfels, Hill Country, South Texas",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          name: "Custom Hunting & Fishing Cover Gallery — Texas Made Shade",
          url: canonicalUrl(PAGE_PATH),
          about: "Custom hunting and fishing covers built in Seguin, Texas — air boat, offshore and guide boat covers, deer blind skins, dog boxes, gun covers and gear bags.",
          image: GALLERY_IMAGES.map((img) => ({
            "@type": "ImageObject",
            contentUrl: absUrl(img.src),
            url: absUrl(img.src),
            caption: img.caption,
            description: img.alt,
            creditText: "Texas Made Shade & Covers",
            creator: { "@type": "Organization", name: "Texas Made Shade & Covers", url: "https://texasmadeshade.com" },
            copyrightNotice: "© Texas Made Shade & Covers",
            license: canonicalUrl(PAGE_PATH),
            acquireLicensePage: canonicalUrl("/contact"),
            contentLocation: { "@type": "Place", name: "Seguin, Texas" },
          })),
        }),
      },
    ],
  }),

  component: () => (
    <ServicePage
      hideLetsTalkForm
      hideFooterPartners
      breadcrumbs={[
        { label: "Custom Canvas & Marine Covers", to: "/covers-tops" },
        { label: "Custom Boat Covers, T-Tops & Biminis", to: "/boat-covers" },
        { label: "Hunting & Fishing Covers — Blinds, Boats, Coolers & Gear" },
      ]}
      eyebrow="★ HUNTING & FISHING ★"
      title="Custom Hunting & Fishing Covers"
      intro="Custom hunting and fishing covers built in Seguin, Texas — deer blinds, bay boats, kayaks, coolers, gun racks, ATVs and UTVs. Patterned to your gear in marine-grade canvas, heavy vinyl and hardware to handle South Texas brush, Gulf Coast salt and seasons of hard use."
      heroImage={heroPicture}
      features={[
        "Deer blind covers, roofs and replacement skins",
        "Bay boat, bass boat, kayak, jon boat, air boat, offshore boat and guide boat covers",
        "Cooler wraps and YETI-style insulated covers",
        "Gun covers, bow and arrow covers and rod rack canvas covers",
        "ATV / UTV / side-by-side cab and bed covers",
        "Sunbrella® Marine canvas and marine vinyl options",
        "Hardware, YKK marine zippers, reinforced corners",
        "On-site patterning at your dock, lease or ranch (for an additional trip charge)",
      ]}
      body={[
        {
          heading: "Custom Deer Blind Covers & Replacement Roof Skins",
          text: "Patterned canvas covers and full soft-skin enclosures for box blinds, tower blinds and tripod stands — built to stop flap, tear and UV fade through long Texas hunting seasons.",
          image: {
            src: truckHighRackBlind,
            alt: "Lifted Ford truck on a South Texas brush-country sendero with a custom camo high-rack hunting blind — camo canvas wrap and roof patterned and sewn by Texas Made Shade",
            caption: "Custom camo high-rack truck blind — wrap and roof patterned in our Seguin shop.",
          },
          bullets: [
            "Replacement roof skins patterned to your blind frame",
            "Full enclosures with zip-down windows and shoot-through ports",
            "Reinforced corners, double-stitched seams and rot-proof thread",
            "Snaps and shock-cord skirt for a tight, quiet fit",
            "On-site patterning across South Texas and the Hill Country is an option with a Trip Fee",
          ],
          cta: {
            label: "See Blind Curtains & Drop Panels",
            href: "/curtains",
          },
        },
        {
          heading: "Air Boat Covers, Offshore Boat Covers, Guide Boat Covers, Bimini Tops & T-Top Canvas",
          text: "Mooring covers, storage covers, console covers, T-top canvas and bimini tops for bay boats, bass boats, jon boats, kayaks, air boats, offshore boats and guide boats — sewn from marine-grade fabric with hardware built for Texas coast humidity and salt.",
          image: {
            src: airboatRedfish,
            alt: "Angler holding a redfish on a camo-wrapped air boat at sunset in a Texas coastal marsh",
            caption: "Air boat redfish trip at sunset on the marsh — the kind of rig we cover, canvas and upholster in our Seguin shop.",
          },
          bullets: [
            "Snap-fit mooring & full storage covers",
            "T-top canvas, leaning post and console covers",
            "Bimini tops with tubing and quick-release hardware",
            "30-gauge clear vinyl windows for visibility under tow",
            "Patterned in our Seguin shop or on-site at your slip",
          ],
          cta: {
            label: "Browse All Boat Covers, T-Tops & Biminis",
            href: "/boat-covers",
          },
        },
        {
          heading: "Cooler Wraps, Gun Covers & Hunting Gear Bags",
          text: "Insulated cooler wraps, gun and bow & arrow covers and field bags sewn in-house on industrial machines — sized to your gear so nothing slides, sags or wears at the seams.",
          image: {
            src: coolerCushion,
            alt: "Custom YETI / RTIC cooler cushion top and insulated wrap sewn in Seguin, TX by Texas Made Shade",
          },
          bullets: [
            "YETI- and RTIC-style insulated cooler wraps",
            "Gun covers, bow and arrow covers and rod rack covers with padded interiors",
            "Decoy bags, shell bags and tie-down gear totes",
            "Heavy bonded thread and reinforced stress points",
          ],
          cta: {
            label: "Visit Our Seguin, TX Shop",
            href: "/seguin",
          },
        },
        {
          heading: "ATV, UTV & Side-by-Side Covers with Roll-Down Weather Curtains",
          text: "Cab covers, bed covers, soft tops, full enclosures and roll-down weather curtains — built to keep wind, rain and cold out of the cab on early hunts and late-season ranch rides.",
          image: {
            src: utvRollCurtain,
            alt: "Black enclosed dirt bike trailer with a custom roll-down vinyl weather curtain and clear vinyl windows, ramp down with a number 21 motocross bike parked beside it on a Texas track",
            caption: "Custom roll-down vinyl curtain with clear windows on an enclosed motocross trailer — built in our Seguin shop.",
          },
          bullets: [
            "Roll-up & roll-down weather curtains — block wind, rain, cold and dust",
            "Clear vinyl windows for full visibility while glassing or driving",
            "YKK zippers, twist-lock fasteners and snaps for one-handed use",
            "Heavy vinyl-coated polyester or marine canvas — UV-stable and mildew-resistant",
            "Patterned to fit Polaris, Can-Am, Kawasaki, John Deere and Kubota cabs",
            "Removable in seconds and stored with sewn-in tie-back straps",
          ],
          cta: {
            label: "See Roll-Down Curtains & Drop Panels",
            href: "/curtains",
          },
        },
        {
          heading: "Welded Dog Boxes & Hunting Truck Upholstery",
          text: "Custom-welded aluminum and steel dog boxes plus marine-vinyl upholstery, headliners and wrap tarps — built start-to-finish in our Seguin fabrication and canvas shop.",
          image: {
            src: huntingTruckHighSeats,
            alt: "Custom hunting truck flatbed rig with welded aluminum high-seat tower, diamond plate toolboxes and orange '7P' canvas identification panel — built by Texas Made Shade in Seguin, TX",
            caption: "Custom hunting truck — welded high-seat tower, dog box, toolboxes and orange ID canvas panel.",
          },
          bullets: [
            "Aluminum & steel dog boxes sized to your truck bed",
            "Vented doors, top storage, gear racks and tie-downs",
            "Marine-vinyl seat re-covers and headliners",
            "Fitted wrap tarps for dog boxes, beds and gear racks",
            "Welded high-seat towers and ID canvas panels",
          ],
        },
      ]}
      applicationsEyebrow="PRODUCTS WE OFFER"
      applicationsHeading="Products We Offer"
      applications={[
        "Deer Blinds",
        "Bay & Bass Boats",
        "Kayaks & Jon Boats",
        "Air Boats",
        "Offshore Boats",
        "Guide Boats",
        "Coolers (YETI / RTIC)",
        "Gun Covers & Bow and Arrow Covers",
        "ATVs & UTVs",
        "Hunting Trailers",
        "Ranch Equipment",
      ]}
      faqs={[
        {
          q: "Do you make custom deer blind covers in Texas?",
          a: "Yes — full canvas deer blind covers, replacement roof skins, and complete soft-skin enclosures for box blinds, tower blinds, and tripod stands. We pattern to your specific blind so the cover doesn't flap or tear at the corners, and we serve hunters across Central Texas, South Texas and the Hill Country.",
          aJsx: (
            <p>
              Yes — full canvas <Link to="/hunting-fishing-covers" className="font-semibold text-navy-deep underline">deer blind covers</Link>, replacement roof skins, and complete soft-skin enclosures for box blinds, tower blinds, and tripod stands. We pattern to your specific blind so the cover doesn't flap or tear at the corners, and we serve hunters across Central Texas and the Hill Country from{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">our Seguin canvas &amp; welding shop</Link>, with frequent deer blind cover jobs in{" "}
              <Link to="/san-antonio" className="font-semibold text-navy-deep underline">San Antonio</Link>,{" "}
              <Link to="/new-braunfels" className="font-semibold text-navy-deep underline">New Braunfels</Link>,{" "}
              <Link to="/boerne" className="font-semibold text-navy-deep underline">Boerne</Link> and{" "}
              <Link to="/kerrville" className="font-semibold text-navy-deep underline">Kerrville</Link>.
            </p>
          ),
        },
        {
          q: "What fabric holds up best for hunting and fishing covers?",
          a: "We have several fabric types to select from and the right choice will depend on the project application. UV exposure, abrasion from brush, salt and humidity, weight, breathability and budget all factor in — tell us how and where the cover will be used and we'll recommend the best fabric for the job.",
          aJsx: (
            <p>
              We carry a wide range of <Link to="/fabrics" className="font-semibold text-navy-deep underline">marine and outdoor fabrics</Link> — Sunbrella® Marine, Top Gun® and Aqualon Edge among others. UV exposure, abrasion from brush, salt and humidity, weight, breathability and budget all factor in. Tell us how and where the cover will be used and we'll recommend the right fabric — see our full{" "}
              <Link to="/fabrics" className="font-semibold text-navy-deep underline">fabric selection</Link> for swatches and specs.
            </p>
          ),
        },
        {
          q: "Can you pattern a boat cover at my dock or lease?",
          a: "Usually on smaller boats the customer brings us the boat so we have it to pattern. In some cases where the boat can not be transported — for example, a large yacht — we come to the dock and pattern on-site.",
          aJsx: (
            <p>
              Usually on smaller boats the customer brings the boat to{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">our Seguin marine canvas shop</Link> so we can pattern in-house. For larger rigs that can't be transported — yachts and offshore boats — we travel to the dock and pattern on-site. See our full{" "}
              <Link to="/boat-covers" className="font-semibold text-navy-deep underline">boat cover services</Link> and{" "}
              <Link to="/covers-tops" className="font-semibold text-navy-deep underline">marine canvas tops</Link>.
            </p>
          ),
        },
        {
          q: "Do weather curtains fit my Polaris, Can-Am, Kawasaki, John Deere or Kubota?",
          a: "Yes — we pattern roll-down weather curtains to your specific UTV or side-by-side cab, including Polaris Ranger, RZR and General, Can-Am Defender and Maverick, Kawasaki Mule and Teryx, John Deere Gator, and Kubota RTV. Bring it to our Seguin shop or send detailed photos and measurements for an off-site build.",
          aJsx: (
            <p>
              Yes — we pattern roll-down <Link to="/curtains" className="font-semibold text-navy-deep underline">UTV weather curtains</Link> to your specific cab, including Polaris Ranger, RZR and General, Can-Am Defender and Maverick, Kawasaki Mule and Teryx, John Deere Gator, and Kubota RTV. Bring it to{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">our Seguin UTV &amp; side-by-side cover shop</Link> or send detailed photos and measurements for an off-site build.
            </p>
          ),
        },
        {
          q: "What materials do you use for UTV weather curtains?",
          a: "We carry a wide range of marine and outdoor fabrics — Sunbrella® Marine, Top Gun®, Aqualon Edge, heavy vinyl-coated polyester, marine vinyl and many other fabrics can be used depending on the build, look and budget you want. Panels are paired with 30-gauge clear vinyl windows, YKK marine zippers, twist-lock fasteners and snaps. All options we recommend are UV-stable, water-repellent and mildew-resistant for South Texas weather.",
          aJsx: (
            <p>
              We carry a wide range of <Link to="/fabrics" className="font-semibold text-navy-deep underline">marine and outdoor fabrics</Link> — Sunbrella® Marine, Top Gun®, Aqualon Edge, heavy vinyl-coated polyester, marine vinyl and many others depending on the build, look and budget you want. Panels are paired with 30-gauge clear vinyl windows, YKK marine zippers, twist-lock fasteners and snaps — all UV-stable, water-repellent and mildew-resistant for South Texas weather.
            </p>
          ),
        },
        {
          q: "How are roll-down weather curtains installed?",
          a: "Curtains attach to your existing cab frame with a combination of snaps, twist-locks and zip-on tracks. They roll up and tie back with sewn-in straps for warm weather, and roll down in seconds when wind, rain or cold rolls in. We include install instructions and can fit them in our shop.",
          aJsx: (
            <p>
              <Link to="/curtains" className="font-semibold text-navy-deep underline">Roll-down curtains</Link> attach to your existing cab frame with a combination of snaps, twist-locks and zip-on tracks. They roll up and tie back with sewn-in straps for warm weather, and roll down in seconds when wind, rain or cold rolls in. We include install instructions and can fit them in{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">our Seguin weather-curtain fitting shop</Link>.
            </p>
          ),
        },
        {
          q: "How much do custom UTV weather curtains cost?",
          a: "Pricing depends on your machine, fabric choice, and whether you want a partial set (front/rear) or a full enclosure with doors. Most weather curtain sets fall between a few hundred and just over a thousand dollars. Send us your make, model and photos for a free written estimate.",
          aJsx: (
            <p>
              Pricing depends on your machine, <Link to="/fabrics" className="font-semibold text-navy-deep underline">fabric choice</Link>, and whether you want a partial set (front/rear) or a full enclosure with doors. Most <Link to="/curtains" className="font-semibold text-navy-deep underline">weather curtain sets</Link> fall between a few hundred and just over a thousand dollars. <Link to="/contact" className="font-semibold text-navy-deep underline">Send us your make, model and photos</Link> for a free written estimate.
            </p>
          ),
        },
        {
          q: "Do you make custom YETI cooler covers, gun covers and bow and arrow covers?",
          a: "Yes — insulated YETI and RTIC cooler wraps, gun covers, bow and arrow covers, rod rack covers, decoy bags, and tie-down hunting gear bags are all sewn in our Seguin shop on industrial machines with heavy marine thread.",
          aJsx: (
            <p>
              Yes — insulated YETI and RTIC cooler wraps, gun covers, bow and arrow covers, rod rack covers, decoy bags, and tie-down hunting gear bags are all sewn in{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">our Seguin industrial sewing shop</Link> on industrial machines with heavy marine thread. See more in our{" "}
              <Link to="/hunting-fishing-covers" className="font-semibold text-navy-deep underline">hunting & fishing cover lineup</Link>.
            </p>
          ),
        },
        {
          q: "Do you weld custom dog boxes for hunting trucks?",
          a: "Yes. We weld custom aluminum and steel dog boxes sized to your truck bed, with vented doors, top storage, gear racks and tie-downs. Pair it with a fitted wrap tarp and marine-vinyl truck upholstery from our shop for a full hunting-truck build. See our welding & custom fabrication page for more.",
          aJsx: (
            <p>
              Yes. We weld custom aluminum and steel dog boxes sized to your truck bed, with vented doors, top storage, gear racks and tie-downs. Pair it with a fitted wrap tarp and marine-vinyl truck upholstery for a full hunting-truck build — see our{" "}
              <Link to="/custom-fabrication" className="font-semibold text-navy-deep underline">welding & custom fabrication services</Link>.
            </p>
          ),
        },
        {
          q: "Can you build T-top canvas, console covers and bimini tops for bay boats?",
          a: "Yes — T-top canvas, console covers, leaning post covers, bimini tops, mooring covers and full storage covers. We carry a vast selection of marine fabrics, including Sunbrella® Marine, Top Gun® and Aqualon®, so we can match the look, weight and budget you need. We work with bay boat, bass boat, flats boat and offshore rigs across the Texas coast — Corpus Christi, Rockport, Port O'Connor, Port Aransas and Galveston.",
          aJsx: (
            <p>
              Yes — <Link to="/covers-tops" className="font-semibold text-navy-deep underline">T-top canvas, console covers, leaning post covers and bimini tops</Link>, plus mooring covers and full storage covers. We carry a vast selection of <Link to="/fabrics" className="font-semibold text-navy-deep underline">marine fabrics</Link>, including Sunbrella® Marine, Top Gun® and Aqualon®. We work with bay boat, bass boat, flats boat and offshore rigs across the Texas coast — including{" "}
              <Link to="/corpus-christi" className="font-semibold text-navy-deep underline">Corpus Christi</Link>,{" "}
              <Link to="/houston" className="font-semibold text-navy-deep underline">Houston</Link> and the Gulf Coast.
            </p>
          ),
        },
        {
          q: "How long does a custom hunting or fishing cover take to build?",
          a: "It depends on the current project and the jobs in front of yours, but a good range is 1–5 weeks — it just varies based on incoming projects. Need it sooner? We offer rush fees on jobs that need to jump the queue.",
          aJsx: (
            <p>
              It depends on the current project and the jobs in front of yours, but a good range is 1–5 weeks — it just varies based on incoming projects. Need it sooner? We offer rush fees on jobs that need to jump the queue —{" "}
              <Link to="/contact" className="font-semibold text-navy-deep underline">contact us with your timeline</Link> and we'll let you know what's possible.
            </p>
          ),
        },
        {
          q: "What Texas areas do you serve for hunting and fishing covers?",
          a: "We're based in Seguin and serve hunters and anglers across Central Texas, South Texas, the Hill Country, and the Gulf Coast — including San Antonio, New Braunfels, Boerne, Kerrville, Fredericksburg, Austin, Houston, Corpus Christi, Rockport and Port O'Connor.",
          aJsx: (
            <p>
              We're based in <Link to="/seguin" className="font-semibold text-navy-deep underline">Seguin</Link> and serve hunters and anglers across Central Texas, South Texas, the Hill Country, and the Gulf Coast — including{" "}
              <Link to="/san-antonio" className="font-semibold text-navy-deep underline">San Antonio hunters and bay-boat anglers</Link>,{" "}
              <Link to="/new-braunfels" className="font-semibold text-navy-deep underline">New Braunfels</Link>,{" "}
              <Link to="/boerne" className="font-semibold text-navy-deep underline">Boerne</Link>,{" "}
              <Link to="/kerrville" className="font-semibold text-navy-deep underline">Kerrville</Link>,{" "}
              <Link to="/fredericksburg" className="font-semibold text-navy-deep underline">Fredericksburg</Link>,{" "}
              <Link to="/austin" className="font-semibold text-navy-deep underline">Austin</Link>,{" "}
              <Link to="/houston" className="font-semibold text-navy-deep underline">Houston</Link> and{" "}
              <Link to="/corpus-christi" className="font-semibold text-navy-deep underline">Corpus Christi</Link>.
            </p>
          ),
        },
        {
          q: "Do you build custom air boat covers for Texas marshes and Hill Country lakes?",
          a: "Yes — we pattern custom air boat covers in Sunbrella® Marine and heavy marine vinyl, fitted around the fan cage, prop guard, console and rigging. Mooring covers, deck covers and console wraps are built in our Seguin shop for fan-cage air boats running South Texas marshes, the Coastal Bend and Hill Country lakes.",
          aJsx: (
            <p>
              Yes — we pattern <Link to="/hunting-fishing-covers" hash="air-boat-covers" className="font-semibold text-navy-deep underline">custom air boat covers</Link> in Sunbrella® Marine and heavy marine vinyl, fitted around the fan cage, prop guard, console and rigging. Mooring covers, deck covers and console wraps are built in{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">our Seguin marine canvas shop</Link> for fan-cage air boats running South Texas marshes, the Coastal Bend and Hill Country lakes.
            </p>
          ),
        },
        {
          q: "What's the best fabric for an air boat mooring cover in South Texas?",
          a: "For air boats we typically recommend Sunbrella® Marine or Top Gun® for UV stability and breathability, or heavy vinyl-coated polyester when abrasion from cane, marsh grass and trailering is the bigger concern. We pair fabric with reinforced wear pads at the cage uprights and prop guard so the cover holds up season after season.",
          aJsx: (
            <p>
              For air boats we typically recommend <Link to="/fabrics" className="font-semibold text-navy-deep underline">Sunbrella® Marine or Top Gun®</Link> for UV stability and breathability, or heavy vinyl-coated polyester when abrasion from cane, marsh grass and trailering is the bigger concern. We pair fabric with reinforced wear pads at the cage uprights and prop guard so the cover holds up season after season — see our full{" "}
              <Link to="/fabrics" className="font-semibold text-navy-deep underline">marine fabric selection</Link>.
            </p>
          ),
        },
        {
          q: "Do you make offshore boat covers for center consoles and sportfishing rigs?",
          a: "Yes — full storage and mooring covers, T-top canvas, helm and console covers, and leaning-post covers for offshore boats and center consoles. We work with Gulf Coast anglers running out of Port Aransas, Port O'Connor, Rockport, Galveston and Corpus Christi, and pattern in our Seguin shop or on-site at the slip for boats too large to transport.",
          aJsx: (
            <p>
              Yes — full storage and mooring covers, <Link to="/covers-tops" className="font-semibold text-navy-deep underline">T-top canvas, helm and console covers</Link>, and leaning-post covers for{" "}
              <Link to="/hunting-fishing-covers" hash="offshore-boat-covers" className="font-semibold text-navy-deep underline">offshore boats and center consoles</Link>. We work with Gulf Coast anglers running out of Port Aransas, Port O'Connor, Rockport, Galveston and{" "}
              <Link to="/corpus-christi" className="font-semibold text-navy-deep underline">Corpus Christi</Link>, and pattern in{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">our Seguin shop</Link> or on-site at the slip for boats too large to transport.
            </p>
          ),
        },
        {
          q: "How do you protect rod holders, outriggers and antennas on an offshore boat cover?",
          a: "We pattern reinforced cutouts and storm flaps around rod holders, outriggers, antennas and T-top hardware so the cover seals tight without trapping water or chafing the gel coat. Reinforced corners, rope channels and tie-down loops keep the cover snapped down through Gulf wind and trailering.",
          aJsx: (
            <p>
              We pattern reinforced cutouts and storm flaps around rod holders, outriggers, antennas and T-top hardware so the cover seals tight without trapping water or chafing the gel coat. Reinforced corners, rope channels and tie-down loops keep the cover snapped down through Gulf wind and trailering — see more{" "}
              <Link to="/boat-covers" className="font-semibold text-navy-deep underline">offshore and center console cover work</Link>.
            </p>
          ),
        },
        {
          q: "Can you build guide boat covers and bimini tops for bay & flats skiffs?",
          a: "Yes — bimini tops, console covers, leaning-post covers, poling-platform covers and snap-on mooring covers for bay and flats guide boats. We build for Texas Coastal Bend guides chasing redfish and trout, with marine canvas, YKK marine zippers and stainless hardware that holds up to daily salt-water use.",
          aJsx: (
            <p>
              Yes — bimini tops, console covers, leaning-post covers, poling-platform covers and snap-on mooring covers for{" "}
              <Link to="/hunting-fishing-covers" hash="guide-boat-covers" className="font-semibold text-navy-deep underline">bay and flats guide boats</Link>. We build for Texas Coastal Bend guides chasing redfish and trout, with{" "}
              <Link to="/fabrics" className="font-semibold text-navy-deep underline">marine canvas</Link>, YKK marine zippers and stainless hardware that holds up to daily salt-water use — explore our full{" "}
              <Link to="/covers-tops" className="font-semibold text-navy-deep underline">bimini top &amp; T-top canvas lineup</Link>.
            </p>
          ),
        },
        {
          q: "How long does a custom air boat, offshore or guide boat cover take to build?",
          a: "Most air boat, offshore and guide boat covers run 1–5 weeks depending on the queue and whether we're patterning in-shop or on-site at your slip. Rush jobs are available with a rush fee — send us your boat, location and timeline for a written estimate.",
          aJsx: (
            <p>
              Most air boat, offshore and guide boat covers run 1–5 weeks depending on the queue and whether we're patterning in-shop or on-site at your slip. Rush jobs are available with a rush fee —{" "}
              <Link to="/contact" className="font-semibold text-navy-deep underline">send us your boat, location and timeline</Link> for a written estimate.
            </p>
          ),
        },
      ]}
      preContent={
        <>
          <section className="border-t-4 border-navy-deep bg-white py-12 lg:py-16">
            <div className="mx-auto max-w-3xl px-4 lg:px-8">
              <figure className="overflow-hidden border-4 border-navy-deep shadow-card">
                <img
                  src={quailTruckSunset}
                  alt="Four South Texas quail hunters in blaze orange vests posed with English pointers and a custom-built dog box truck rig at sunset in the South Texas brush country"
                  width={1600}
                  height={1067}
                  loading="eager"
                  decoding="async"
                  className="aspect-[3/2] w-full object-cover"
                />
                <figcaption className="bg-navy-deep px-4 py-3 font-display text-[11px] uppercase tracking-[0.2em] text-white">
                  South Texas quail hunt — custom dog box truck rig built in our Seguin shop
                </figcaption>
              </figure>
            </div>
          </section>
          <HuntingFishingExplorer />
        </>
      }
    >
      {/* ============ FEATURED HUNTING TRUCK ============ */}
      <section className="border-t-4 border-navy-deep bg-cream py-16">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <figure className="sm:float-right sm:ml-6 sm:mb-3 sm:w-[46%] md:w-[42%] lg:w-[38%] overflow-hidden border-4 border-navy-deep shadow-card">
            <img
              src={huntingTruckHighSeats}
              alt="Custom hunting truck flatbed rig with welded aluminum high-seat tower, diamond plate toolboxes and bright orange '7P' canvas identification panel — built by Texas Made Shade in Seguin, TX"
              width={1200}
              height={1600}
              loading="lazy"
              decoding="async"
              className="block h-auto w-full object-cover"
            />
            <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-[0.2em] text-white">
              Custom hunting truck — high seats, dog box &amp; orange canvas ID panel
            </figcaption>
          </figure>
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Featured Hunting Build</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Custom Hunting Trucks — Welded Dog Boxes, High Seats &amp; Canvas ID Panels
          </h2>
          <p className="mt-4 text-lg text-navy-deep/80">
            This South Texas hunting truck rig was built start-to-finish in our Seguin shop —
            welded aluminum high-seat tower, diamond plate toolboxes, custom dog box and a
            heavy bright-orange &ldquo;7P&rdquo; canvas identification panel patterned in
            marine-grade Sunbrella® and finished with brass grommets. One shop, one build,
            no outsourcing.
          </p>

          <h3 className="mt-6 font-display text-xl font-bold text-navy-deep md:text-2xl">
            Built for South Texas Brush Country &amp; Hill Country Leases
          </h3>
          <p className="mt-3 text-lg text-navy-deep/80">
            Quail outfitters, dove guides, dog handlers and ranch managers across South
            Texas, the Coastal Bend and the Hill Country come to us for fully welded hunting
            trucks — fabrication, marine-vinyl upholstery and canvas wrap tarps under one roof
            in Seguin. Every panel is patterned to the exact frame so it stays drum-tight at
            highway speed.
          </p>

          <h3 className="mt-6 font-display text-xl font-bold text-navy-deep md:text-2xl">
            What&apos;s Included on a Custom Hunting Truck Build
          </h3>
          <ul className="mt-3 space-y-2 text-base text-navy-deep/80">
            <li>★ Welded aluminum high-seat towers &amp; safety rails</li>
            <li>★ Custom dog boxes — vented kennel doors &amp; locking gun racks</li>
            <li>★ Diamond plate toolboxes &amp; flatbed fabrication</li>
            <li>★ Heavy canvas wrap tarps &amp; orange ID panels with brass grommets</li>
            <li>★ Marine-vinyl seat re-covers, headliners &amp; gear bags</li>
          </ul>
          <div className="clear-both" />
        </div>
      </section>

      {/* ============ QUOTE FORMS ============ */}
      <BoatCoverForm />
      <CoolerCushionForm />
      <HuntingTarpForm />

      <section className="border-t-4 border-navy-deep bg-white py-20">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="text-center">
            <div className="font-display text-sm tracking-[0.3em] text-red">★ FISHING ★</div>
            <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
              Built for the Anglers
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-lg text-muted-foreground">
              Texas Made Shade &amp; Covers builds custom gear for fishermen across the Texas coast and inland lakes —
              boat covers, tarps, cushions, console covers, T-tops and shade products from the bay to the back lake.
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-3xl gap-6 sm:grid-cols-2">
            <figure className="overflow-hidden border-4 border-navy-deep shadow-card">
              <img
                src={fishingTrout}
                alt="Limit of speckled trout laid out on a bay boat deck after a Texas coast fishing trip"
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] w-full object-cover"
              />
              <figcaption className="bg-navy-deep px-4 py-2 font-display text-[11px] uppercase tracking-[0.2em] text-white">
                Speckled trout limit — Texas Coastal Bend
              </figcaption>
            </figure>
            <figure className="overflow-hidden border-4 border-navy-deep shadow-card">
              <img
                src={fishingRedfish}
                alt="Angler holding a redfish under a custom canvas T-top on a Texas bay boat"
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] w-full object-cover"
              />
              <figcaption className="bg-navy-deep px-4 py-2 font-display text-[11px] uppercase tracking-[0.2em] text-white">
                Redfish under a custom T-top cover
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* ============ HUNTING PROJECT GALLERY ============ */}
      <section id="hunting-gallery" className="scroll-mt-24 border-t-4 border-navy-deep bg-cream py-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Hunting Project Gallery</div>
          <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
            Custom Hunting Builds We've Made
          </h2>
          <p className="mt-4 max-w-3xl text-lg text-navy-deep/80">
            Looking for a <strong>custom hunting product</strong>? We've sewn just about everything Texas hunters
            have asked for — from <strong>quail hunting bags</strong> and{" "}
            <strong>sandhill crane decoy bags</strong>, to heavy canvas <strong>tarps for duck hunting boats</strong>,
            deer blind skins and gear bags for the lease. If you can describe it or sketch it, we can pattern it
            and build it in our Seguin shop.
          </p>
          <ul
            role="list"
            aria-label="Custom hunting builds gallery"
            className="mt-10 grid list-none gap-5 p-0 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6"
          >
            {[
              { src: huntingDeerBlind, alt: "Custom tan Sunbrella canvas cover patterned to a Texas box deer blind, with reinforced corners and tie-downs, set in South Texas brush country at golden-hour sunset", label: "Deer Blind Cover", caption: "Patterned box-blind skin in heavy Sunbrella canvas — reinforced corners, tie-downs and weatherproof stitching built to last season after season." },
              { src: huntingDuckBoat, alt: "Heavy navy canvas tarp covering a duck hunting boat on a Texas marsh at dawn", label: "Duck Boat Tarp", caption: "Full-coverage canvas tarp built to protect a duck hunting rig between hunts." },
              { src: huntingQuailBag, alt: "Custom canvas hunting bag with leather trim and shotgun shells — specialty hunting items by Texas Made Shade", label: "Specialty Hunting Items", caption: "Canvas bags, arrow bags, shell bags and one-off custom gear." },
              { src: huntingCraneDecoy, alt: "Custom canvas game bird decoy bag full of sandhill crane decoys in a Texas field at sunrise", label: "Game Bird Decoy Bag", caption: "Heavy-duty bag built to haul a full spread of sandhill crane, quail and dove decoys." },
              { src: huntingBowhunter, alt: "Bowhunter at full draw at night on a Texas range — Texas Made Shade hunting customer", label: "Bow & Arrow Products", caption: "Built for the hunters and bowhunters who put our gear to the test." },
            ].map((img) => (
              <li key={img.src}>
                <figure className="group overflow-hidden border-2 border-navy-deep/10 bg-cream shadow-card">
                  <img
                    src={img.src}
                    alt={img.alt}
                    width={1200}
                    height={900}
                    loading="lazy"
                    decoding="async"
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    className="aspect-[4/3] w-full object-cover transition duration-300 group-hover:scale-105"
                  />
                  <figcaption className="border-t-2 border-navy-deep/10 bg-white px-4 py-3">
                    <div className="font-display text-sm font-bold text-navy-deep">{img.label}</div>
                    <div className="text-xs text-muted-foreground">{img.caption}</div>
                  </figcaption>
                </figure>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ RELATED SERVICES + LOCATION ============ */}
      <section className="border-t-4 border-navy-deep bg-white py-16">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="font-display text-sm tracking-[0.3em] text-red">★ Related Hunting &amp; Fishing Services</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Explore More Custom Canvas, Welding &amp; Marine Work
          </h2>
          <p className="mt-3 max-w-3xl text-muted-foreground">
            Hunters and anglers across Texas pair these services with custom hunting and fishing covers — boat tops,
            welded dog boxes, hunting truck upholstery, ranch trailer tarps and more. Built in Seguin, installed across Texas.
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              { to: "/boat-covers", title: "Custom Fishing Boat Covers, T-Tops & Bimini Tops in Texas", desc: "Mooring covers, console covers and T-top canvas for bay boats, bass boats and offshore rigs." },
              { to: "/covers-tops", title: "Marine Canvas, Snap Covers & Replacement Boat Tops", desc: "Patterned marine canvas, snap covers and replacement tops in Sunbrella® Marine fabric." },
              { to: "/dog-boxes", title: "Custom Dog Boxes & Hunting Truck Builds", desc: "Welded aluminum and steel dog boxes, gear racks and full hunting-truck builds." },
              { to: "/custom-fabrication", title: "Welding & Custom Fabrication for Hunters & Ranchers", desc: "Welded gun racks, gear racks and ranch fabrication in our Seguin shop." },
              { to: "/trailer-covers", title: "Heavy-Duty Hunting Trailer Tarps & Ag Equipment Covers", desc: "Heavy-duty fitted tarps for hunting trailers, ranch equipment and stock trailers." },
              { to: "/stock-trailer-covers", title: "Custom Stock Trailer Covers for Ranch & Hunting Hauling", desc: "Custom-patterned stock trailer covers for ranch and hunting hauling across South Texas." },
              { to: "/wagon-covers", title: "Hunting Wagon, Mule Buggy & Lease Vehicle Covers", desc: "Canvas covers for hunting wagons, mule buggies and lease vehicles." },
              { to: "/rv-covers", title: "Hunting Camp RV & Travel-Trailer Covers Built for Texas Sun", desc: "Hunting-camp RV and travel-trailer covers built to handle Texas sun and brush." },
              { to: "/curtains", title: "Deer Blind Curtains, Roll-Up Panels & Shoot-Through Mesh", desc: "Roll curtains, screen mesh and shoot-through panels for box and tower deer blinds." },
              { to: "/fabrics", title: "Shop Sunbrella® Marine Canvas & Heavy-Duty Vinyl Fabrics", desc: "Browse the marine canvas, vinyl and thread we use on every hunting and fishing build." },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="group block border-l-4 border-red bg-white p-5 shadow-card transition hover:shadow-bold"
              >
                <div className="font-display text-base font-bold text-navy-deep group-hover:text-red">{l.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{l.desc}</p>
              </Link>
            ))}
          </div>
          <div className="mt-10 grid gap-6 border-t border-navy-deep/15 pt-8 lg:grid-cols-2">
            <div className="border-l-4 border-red bg-white p-6 shadow-card">
              <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Visit Our Seguin Shop</div>
              <h3 className="mt-2 font-display text-2xl font-bold text-navy-deep md:text-3xl">
                Texas Made Shade — Hunting &amp; Fishing Cover HQ
              </h3>
              <p className="mt-2 text-muted-foreground">
                Drop off a deer blind, boat top, cooler or trailer for a custom pattern.
              </p>
              <address className="mt-4 not-italic text-navy-deep">
                <div className="font-bold">Texas Made Shade</div>
                <div>959 East Kingsbury Street</div>
                <div>Seguin, TX 78155</div>
                <div className="mt-2">Mon–Thu 8:30a – 5:00p · Fri 8:30a – 4:00p</div>
              </address>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href="tel:+18303909588" className="inline-flex items-center bg-red px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-red/90">
                  Call +1 (830) 390-9588
                </a>
                <a href="https://www.google.com/maps/dir/?api=1&destination=959+East+Kingsbury+Street+Seguin+TX+78155" target="_blank" rel="noopener noreferrer" className="inline-flex items-center border-2 border-navy-deep px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-navy-deep hover:bg-navy-deep hover:text-white">
                  Get Directions
                </a>
                <Link to="/seguin" className="inline-flex items-center border-2 border-red px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-red hover:bg-red hover:text-white">
                  Seguin Location Page
                </Link>
              </div>
            </div>
            <div className="overflow-hidden border-l-4 border-navy-deep shadow-card">
              <iframe
                title="Texas Made Shade — 959 East Kingsbury Street, Seguin, TX"
                src="https://www.google.com/maps?q=959+East+Kingsbury+Street+Seguin+TX+78155&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "320px" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ anchor (FAQ section is rendered by ServicePage from `faqs` prop) */}
      <div id="hunting-fishing-faq" className="scroll-mt-24" aria-hidden="true" />
    </ServicePage>
  ),
});
